import React, { useMemo } from 'react';
import { getBilingualTutorSessions, calculateSessionProgress } from '../data/bilingualTutorData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const BilingualTutorPage = ({ language = 'traditional', onBack, onSessionClick }) => {
  const { data: remote } = useSupabaseData({
    table: 'eng_bilingualtutor',
    mapRow: (row) => row,
    getFallback: () => null
  });

  const sessions = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) return getBilingualTutorSessions();
    const rows = Array.isArray(remote) ? remote : [remote];
    return rows.filter(r => r.data_type === 'session').map((r) => ({
      id: r.session_id || r.id,
      title: {
        traditional: r.session_title_traditional || '',
        simplified: r.session_title_simplified || '',
        english: r.session_title_english || ''
      },
      description: {
        traditional: r.session_description_traditional || '',
        simplified: r.session_description_simplified || '',
        english: r.session_description_english || ''
      },
      duration: r.duration || '15 min',
      difficulty: r.difficulty || 'beginner',
      isUnlocked: r.is_unlocked !== false,
      isCompleted: Boolean(r.is_completed),
      topics: Array.isArray(r.topics) ? r.topics : [],
      languageOptions: Array.isArray(r.language_options) ? r.language_options : ['english', 'taiwanese']
    }));
  }, [remote]);

  const progress = useMemo(() => {
    if (Array.isArray(remote)) {
      const sessionRows = remote.filter(r => r.data_type === 'session');
      if (sessionRows.length > 0) {
        const completed = sessionRows.filter(r => r.is_completed).length;
        const total = sessionRows.length;
        const percentage = total ? Math.round((completed / total) * 100) : 0;
        return { completed, total, percentage };
      }
    }
    return calculateSessionProgress();
  }, [remote, sessions]);

  const getText = (textObj) => {
    return textObj[language] || textObj.traditional;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-500/20 text-green-300';
      case 'intermediate':
        return 'bg-yellow-500/20 text-yellow-300';
      case 'advanced':
        return 'bg-red-500/20 text-red-300';
      default:
        return 'bg-gray-500/20 text-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white">
            ←
          </button>
          <h1 className="text-xl font-bold">
            {language === 'traditional' ? '雙語導師的制度' :
             language === 'simplified' ? '双语导师的制度' :
             'Bilingual Tutor System'}
          </h1>
          <div className="w-8"></div> {/* Spacer for centering */}
        </div>

        {/* Progress Overview */}
        <div className="bg-gray-800/30 border border-gray-600 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold">
              {language === 'traditional' ? '學習進度' :
               language === 'simplified' ? '学习进度' :
               'Learning Progress'}
            </h2>
            <span className="text-sm text-gray-400">
              {progress.completed}/{progress.total}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {language === 'traditional' ? `${progress.percentage}% 完成` :
             language === 'simplified' ? `${progress.percentage}% 完成` :
             `${progress.percentage}% Complete`}
          </p>
        </div>

        {/* Description */}
        <div className="mb-6 text-center">
          <p className="text-gray-300 text-sm">
            {language === 'traditional' ? '與 ELSA 雙語導師進行互動式英語學習' :
             language === 'simplified' ? '与 ELSA 双语导师进行互动式英语学习' :
             'Interactive English learning with ELSA bilingual tutor'}
          </p>
        </div>

        {/* Sessions List */}
        <div className="space-y-4 mb-20">
          {sessions.map((session) => (
            <div
              key={session.id}
              onClick={() => session.isUnlocked && onSessionClick(session.id)}
              className={`bg-gray-800/30 border border-gray-600 rounded-xl p-4 cursor-pointer transition-all ${
                session.isUnlocked 
                  ? 'hover:bg-gray-700/50 hover:border-gray-500' 
                  : 'opacity-50 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center space-x-4">
                {/* Session Icon */}
                <div className="text-3xl flex-shrink-0">
                  🎤
                </div>
                
                {/* Session Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-semibold text-white">
                      {getText(session.title)}
                    </h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(session.difficulty)}`}>
                      {session.difficulty.toUpperCase()}
                    </span>
                  </div>
                  
                  <p className="text-gray-400 text-sm mb-2">
                    {getText(session.description)}
                  </p>
                  
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>⏱️ {session.duration}</span>
                    <span>🌍 {session.languageOptions.length} {language === 'traditional' ? '語言' : language === 'simplified' ? '语言' : 'languages'}</span>
                  </div>
                </div>
                
                {/* Status */}
                <div className="flex flex-col items-end">
                  {session.isUnlocked ? (
                    session.isCompleted ? (
                      <div className="text-green-400 text-sm">✓ {language === 'traditional' ? '已完成' : language === 'simplified' ? '已完成' : 'Completed'}</div>
                    ) : (
                      <div className="text-blue-400 text-sm">→ {language === 'traditional' ? '開始學習' : language === 'simplified' ? '开始学习' : 'Start Learning'}</div>
                    )
                  ) : (
                    <div className="text-gray-500 text-sm">🔒 {language === 'traditional' ? '鎖定' : language === 'simplified' ? '锁定' : 'Locked'}</div>
                  )}
                </div>
              </div>
              
              {/* Topics Preview */}
              {session.isUnlocked && (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <div className="flex flex-wrap gap-1">
                    {session.topics.slice(0, 3).map((topic, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs"
                      >
                        {topic}
                      </span>
                    ))}
                    {session.topics.length > 3 && (
                      <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs">
                        +{session.topics.length - 3} {language === 'traditional' ? '更多' : language === 'simplified' ? '更多' : 'more'}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BilingualTutorPage;
